import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CurrencyInr,
  Target,
  TrendUp,
  Calculator,
  Trophy,
  ChartLineUp,
  Info,
  Download,
  Lightning,
} from "@phosphor-icons/react";
import api from "../../lib/api";
import { formatINR, formatTenure } from "../../lib/loanMath";

const SOURCE_OPTIONS = [
  "Annual Bonus",
  "Incentive",
  "FD/Investment Maturity",
  "Asset Sale",
  "Inheritance/Gift",
  "Other",
];
const SLABS = [0, 5, 10, 20, 30];

const STRATEGY_LABEL = {
  recommended: "Recommended",
  avalanche: "Highest Interest",
  snowball: "Smallest First",
  max_emi_relief: "Max EMI Relief",
};

/**
 * Bonus / Lump-sum Advisor — recommends which loans to prepay when a user
 * receives a bulk amount. Four strategies compared side-by-side.
 */
export default function BonusAdvisor({ loans, planName, onBalancesUpdated, forceOpen }) {
  const [open, setOpen] = useState(!!forceOpen);
  const [form, setForm] = useState({
    lumpsum_amount: 500000,
    emergency_fund: 0,
    source: "",
    strategy_preference: "reduce_tenure",
    include_tax_benefits: false,
    regime: "Old",
    slab_pct: 0,
  });
  const [included, setIncluded] = useState(() => (loans || []).map(() => true));
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [applying, setApplying] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState("recommended");

  const updateForm = (patch) => setForm((f) => ({ ...f, ...patch }));

  const toggleIncluded = (i) =>
    setIncluded((inc) => inc.map((v, idx) => (idx === i ? !v : v)));

  const canRun = useMemo(() => {
    const money = Number(form.lumpsum_amount) - Number(form.emergency_fund);
    return money > 0 && (loans || []).some((_, i) => included[i]);
  }, [form, loans, included]);

  const run = async () => {
    if (!canRun) {
      toast.error("Please enter a lump sum larger than your emergency fund.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const payload = {
        lumpsum_amount: Number(form.lumpsum_amount),
        emergency_fund: Number(form.emergency_fund),
        source: form.source || null,
        strategy_preference: form.strategy_preference,
        include_tax_benefits: form.include_tax_benefits,
        regime: form.regime,
        slab_pct: Number(form.slab_pct),
        loans: (loans || []).map((l, i) => ({
          id: l.id,
          name: l.name,
          kind: l.kind || "loan",
          loan_type: l.loan_type || "",
          lender_name: l.lender_name || "",
          rate_type: l.rate_type || "Floating",
          principal: Number(l.principal) || 0,
          current_balance: Number(l.current_balance) || Number(l.principal) || 0,
          interest_rate_pct: Number(l.interest_rate_pct) || 0,
          tenure_years: Number(l.tenure_years) || 0,
          current_emi: Number(l.current_emi) || 0,
          remaining_tenure_months: Number(l.remaining_tenure_months) || null,
          prepayment_charge_pct: Number(l.prepayment_charge_pct) || 0,
          lock_in_end_date: l.lock_in_end_date || null,
          min_prepayment_amount: Number(l.min_prepayment_amount) || 0,
          tax_benefit_eligible: l.tax_benefit_eligible || "No",
          property_occupancy: l.property_occupancy || "Not applicable",
          include: !!included[i],
        })),
      };
      const { data } = await api.post("/loans/bonus-advise", payload);
      setResult(data);
      setSelectedStrategy("recommended");
    } catch (e) {
      toast.error("Advisor failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(false);
    }
  };

  const applyPlan = async (updateBalances) => {
    if (!result) return;
    if (updateBalances) {
      const ok = window.confirm(
        "This will reduce the outstanding balances on your prepaid loans in this plan. Continue?"
      );
      if (!ok) return;
    }
    setApplying(true);
    try {
      const { data } = await api.post("/loans/apply-plan", {
        plan_name: planName || "My loan plan",
        label: `Bonus Advisor · ${new Date().toLocaleDateString("en-IN")}`,
        update_balances: !!updateBalances,
        advice_result: result,
      });
      if (data.loans_updated) {
        toast.success(`Scenario saved · ${data.loans_updated} loan balances updated`);
        onBalancesUpdated?.();
      } else {
        toast.success("Scenario saved. Open from 'Saved plans' later.");
      }
    } catch (e) {
      toast.error("Could not save: " + (e.response?.data?.detail || e.message));
    } finally {
      setApplying(false);
    }
  };

  const activeResult =
    selectedStrategy === "recommended"
      ? result?.recommended
      : result?.all_strategies?.[selectedStrategy];

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="btn-ghost px-3 py-2 text-xs inline-flex items-center gap-1.5 border hairline rounded-md"
        data-testid="bonus-advisor-open"
      >
        <Target size={12} weight="duotone" /> Bonus Advisor
      </button>
    );
  }

  return (
    <section
      className="rounded-lg border hairline p-5 mt-6 bg-card space-y-5"
      data-testid="bonus-advisor-section"
    >
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-serif text-xl flex items-center gap-2">
            <Target size={20} weight="duotone" className="text-[hsl(var(--primary))]" />
            Bonus / Lump-sum Advisor
          </div>
          <p className="text-xs text-muted-foreground mt-1 max-w-xl">
            Got a bonus, maturity or sale proceeds? Estima tells you which loan(s)
            to prepay for the best interest saving and monthly cash relief.
          </p>
        </div>
        {!forceOpen && (
          <button
            onClick={() => setOpen(false)}
            className="btn-ghost text-xs px-3 py-1.5"
            data-testid="bonus-advisor-close"
          >
            Hide
          </button>
        )}
      </header>

      {/* Input form */}
      <div className="grid md:grid-cols-2 gap-4" data-testid="bonus-advisor-form">
        <NumField
          label="Lump-sum amount (₹)"
          value={form.lumpsum_amount}
          onChange={(v) => updateForm({ lumpsum_amount: v })}
          testid="bonus-lumpsum"
          hint="The total cash you have in hand."
        />
        <NumField
          label="Emergency fund to keep aside (₹)"
          value={form.emergency_fund}
          onChange={(v) => updateForm({ emergency_fund: v })}
          testid="bonus-emergency"
          hint="We'll only allocate (lump sum − emergency)."
        />
        <SelectField
          label="Source of money"
          value={form.source}
          onChange={(v) => updateForm({ source: v })}
          options={["", ...SOURCE_OPTIONS]}
          placeholder="— optional —"
          testid="bonus-source"
        />
        <SelectField
          label="After prepayment, prefer to"
          value={form.strategy_preference}
          onChange={(v) => updateForm({ strategy_preference: v })}
          options={["reduce_tenure", "reduce_emi"]}
          labels={{
            reduce_tenure: "Reduce tenure (keep EMI, saves most interest)",
            reduce_emi: "Reduce EMI (keep tenure)",
          }}
          testid="bonus-strategy-pref"
        />
        <div className="md:col-span-2 flex items-center gap-6 flex-wrap pt-1">
          <label className="inline-flex items-center gap-2 text-xs">
            <input
              type="checkbox"
              checked={form.include_tax_benefits}
              onChange={(e) => updateForm({ include_tax_benefits: e.target.checked })}
              data-testid="bonus-tax-toggle"
            />
            Include tax benefits in comparison
          </label>
          {form.include_tax_benefits && (
            <>
              <label className="inline-flex items-center gap-1 text-xs">
                Regime:
                <select
                  value={form.regime}
                  onChange={(e) => updateForm({ regime: e.target.value })}
                  className="bg-background border hairline rounded px-2 py-1"
                  data-testid="bonus-regime"
                >
                  <option>Old</option>
                  <option>New</option>
                </select>
              </label>
              <label className="inline-flex items-center gap-1 text-xs">
                Slab:
                <select
                  value={form.slab_pct}
                  onChange={(e) => updateForm({ slab_pct: Number(e.target.value) })}
                  className="bg-background border hairline rounded px-2 py-1"
                  data-testid="bonus-slab"
                >
                  {SLABS.map((s) => (
                    <option key={s} value={s}>{s}%</option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
      </div>

      {/* Loan selection */}
      {!!loans?.length && (
        <div data-testid="bonus-advisor-loan-list">
          <div className="text-xs text-muted-foreground uppercase tracking-wide mb-2">
            Loans to include
          </div>
          <div className="flex flex-wrap gap-2">
            {loans.map((l, i) => (
              <label
                key={l.id || i}
                className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-full border hairline cursor-pointer ${
                  included[i]
                    ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40"
                    : "text-muted-foreground"
                }`}
                data-testid={`bonus-loan-chip-${i}`}
              >
                <input
                  type="checkbox"
                  checked={!!included[i]}
                  onChange={() => toggleIncluded(i)}
                  className="hidden"
                />
                {l.name || `Loan ${i + 1}`}
                <span className="opacity-60">·</span>
                <span>{Number(l.interest_rate_pct).toFixed(2)}%</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <div className="flex gap-3 flex-wrap">
        <button
          onClick={run}
          disabled={!canRun || busy}
          className="btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm"
          data-testid="bonus-advisor-run"
        >
          <Lightning size={14} weight="bold" />
          {busy ? "Analysing…" : "Get recommendation"}
        </button>
      </div>

      {/* ============================================================
          RESULT PANEL
         ============================================================ */}
      {result && (
        <div className="space-y-5 pt-2 border-t hairline" data-testid="bonus-advisor-result">
          {/* Headline */}
          <div
            className="rounded-md bg-[hsl(var(--primary))] text-primary-foreground p-5"
            data-testid="bonus-advisor-headline"
          >
            <div className="flex items-start gap-3">
              <Trophy size={28} weight="duotone" className="shrink-0 opacity-90" />
              <div>
                <div className="text-xs opacity-80 uppercase tracking-wide mb-1">
                  Recommendation
                </div>
                <div className="font-serif text-xl leading-snug">
                  {result.narrative.headline}
                </div>
              </div>
            </div>
          </div>

          {/* Strategy picker tabs */}
          <div className="flex flex-wrap gap-2 border-b hairline pb-3" data-testid="bonus-strategy-tabs">
            {Object.keys(STRATEGY_LABEL).map((s) => (
              <button
                key={s}
                onClick={() => setSelectedStrategy(s)}
                className={`text-xs px-3 py-1.5 rounded-md border hairline ${
                  s === selectedStrategy
                    ? "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/40"
                    : "text-muted-foreground"
                }`}
                data-testid={`bonus-strategy-tab-${s}`}
              >
                {STRATEGY_LABEL[s]}
              </button>
            ))}
          </div>

          {/* Allocation table */}
          <AllocationTable
            allocations={activeResult?.allocations || []}
            preference={result.preference}
          />

          {/* Strategy comparison */}
          <StrategyComparison comparison={result.comparison} />

          {/* Chart */}
          <OutstandingChart chart={result.chart} />

          {/* Why this loan */}
          {!!result.narrative.per_loan_reasons?.length && (
            <div className="rounded-md border hairline p-4 bg-[hsl(var(--muted))]/20" data-testid="bonus-advisor-why">
              <div className="font-serif text-sm mb-2 flex items-center gap-2">
                <Info size={14} weight="duotone" /> Why these loans, in this order?
              </div>
              <ul className="space-y-1.5 text-xs text-muted-foreground">
                {result.narrative.per_loan_reasons.map((reason) => (
                  <li key={reason}>• {reason}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Nudges */}
          {!!result.nudges?.length && (
            <div className="rounded-md border hairline p-4 bg-amber-500/5 border-amber-500/30" data-testid="bonus-advisor-nudges">
              <div className="font-serif text-sm mb-2 text-amber-700 flex items-center gap-2">
                <Info size={14} weight="duotone" /> Smart nudges
              </div>
              <ul className="space-y-1.5 text-xs text-amber-700">
                {result.nudges.map((n) => (
                  <li key={n}>• {n}</li>
                ))}
              </ul>
            </div>
          )}

          {result.tax_disclaimer && (
            <div className="text-[11px] text-muted-foreground italic">
              {result.tax_disclaimer}
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 flex-wrap pt-2 border-t hairline">
            <button
              onClick={() => applyPlan(true)}
              disabled={applying}
              className="btn-primary inline-flex items-center gap-2 px-5 py-2 text-sm"
              data-testid="bonus-advisor-apply"
            >
              <Target size={14} weight="bold" />
              {applying ? "Saving…" : "Apply this plan"}
            </button>
            <button
              onClick={() => applyPlan(false)}
              disabled={applying}
              className="btn-ghost inline-flex items-center gap-2 px-5 py-2 text-sm border hairline rounded-md"
              data-testid="bonus-advisor-save-only"
            >
              <Calculator size={14} />
              Save scenario only
            </button>
            <button
              onClick={() => toast.info("Simulator opens in Feature 3 (coming next)")}
              className="btn-ghost inline-flex items-center gap-2 px-5 py-2 text-sm border hairline rounded-md"
              data-testid="bonus-advisor-simulator"
            >
              <TrendUp size={14} /> Open in Simulator
            </button>
            <button
              onClick={() => downloadPdfSummary(result)}
              className="btn-ghost inline-flex items-center gap-2 px-5 py-2 text-sm border hairline rounded-md"
              data-testid="bonus-advisor-pdf"
            >
              <Download size={14} /> Download PDF
            </button>
          </div>

          <div className="text-[11px] text-muted-foreground italic">
            {result.disclaimer}
          </div>
        </div>
      )}
    </section>
  );
}

// ---------- sub-components ----------
function NumField({ label, value, onChange, testid, hint }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <div className="relative mt-1">
        <CurrencyInr size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          type="number"
          inputMode="numeric"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-background border hairline rounded-md pl-8 pr-3 py-2 text-sm"
          data-testid={testid}
        />
      </div>
      {hint && <span className="text-[11px] text-muted-foreground">{hint}</span>}
    </label>
  );
}

function SelectField({ label, value, onChange, options, labels, placeholder, testid }) {
  return (
    <label className="block">
      <span className="text-xs text-muted-foreground uppercase tracking-wide">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-background border hairline rounded-md px-3 py-2 text-sm"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o || "empty"} value={o}>
            {o === "" ? placeholder || "— choose —" : labels?.[o] || o}
          </option>
        ))}
      </select>
    </label>
  );
}

function AllocationTable({ allocations, preference }) {
  if (!allocations.length) return null;
  return (
    <div className="overflow-x-auto border hairline rounded-md" data-testid="bonus-allocation-table">
      <table className="w-full text-xs">
        <thead className="bg-[hsl(var(--muted))]/40 text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Loan</th>
            <th className="text-right px-3 py-2">Rate</th>
            <th className="text-right px-3 py-2">Pay now</th>
            <th className="text-right px-3 py-2">Charge</th>
            <th className="text-right px-3 py-2">Interest saved</th>
            <th className="text-right px-3 py-2">Net saving</th>
            <th className="text-right px-3 py-2">
              {preference === "reduce_emi" ? "New EMI" : "Months saved"}
            </th>
            <th className="text-right px-3 py-2">Status</th>
          </tr>
        </thead>
        <tbody>
          {allocations.map((a) => (
            <tr key={a.id || a.name} className="border-t hairline" data-testid={`bonus-alloc-row-${(a.name||'').replace(/\s/g,'-')}`}>
              <td className="px-3 py-2 font-serif">
                {a.name}
                {a.eligibility && (
                  <div className="text-[10px] text-red-500 mt-0.5">{a.eligibility}</div>
                )}
              </td>
              <td className="px-3 py-2 text-right">
                {a.effective_rate.toFixed(2)}%
                {Math.abs(a.effective_rate - a.interest_rate_pct) > 0.001 && (
                  <div className="text-[10px] text-muted-foreground">(nominal {a.interest_rate_pct.toFixed(2)}%)</div>
                )}
              </td>
              <td className="px-3 py-2 text-right font-mono">{a.pay_now > 0 ? formatINR(a.pay_now) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{a.charge > 0 ? formatINR(a.charge) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono">{a.interest_saved > 0 ? formatINR(a.interest_saved, true) : "—"}</td>
              <td className="px-3 py-2 text-right font-mono text-[hsl(var(--primary))]">
                {a.net_saving > 0 ? formatINR(a.net_saving, true) : "—"}
              </td>
              <td className="px-3 py-2 text-right font-mono">
                {preference === "reduce_emi"
                  ? (a.new_emi ? formatINR(a.new_emi) : "—")
                  : (a.months_saved ? `${a.months_saved} mo` : "—")}
              </td>
              <td className="px-3 py-2 text-right">
                {a.closed ? (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600 border border-emerald-500/30">
                    Closed
                  </span>
                ) : a.pay_now > 0 ? (
                  <span className="text-[11px] px-1.5 py-0.5 rounded-full bg-sky-500/20 text-sky-700 border border-sky-500/30">
                    Part-paid
                  </span>
                ) : (
                  <span className="text-[11px] text-muted-foreground">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StrategyComparison({ comparison }) {
  return (
    <div className="overflow-x-auto border hairline rounded-md" data-testid="bonus-strategy-comparison">
      <table className="w-full text-xs">
        <thead className="bg-[hsl(var(--muted))]/40 text-muted-foreground">
          <tr>
            <th className="text-left px-3 py-2">Compare across strategies</th>
            {comparison.strategies.map((s) => (
              <th key={s} className="text-right px-3 py-2">{STRATEGY_LABEL[s]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {comparison.rows.map((row) => (
            <tr key={row.label} className="border-t hairline" data-testid={`bonus-compare-row-${row.label.replace(/\s/g,'-')}`}>
              <td className="px-3 py-2 font-serif">{row.label}</td>
              {row.values.map((v, i) => {
                const isBest = row.best[i];
                const display =
                  row.label.includes("Debt-free")
                    ? (v ? new Date(v).toLocaleDateString("en-IN", { month: "short", year: "numeric" }) : "—")
                    : row.label.includes("saving") || row.label.includes("freed")
                      ? formatINR(v, true)
                      : v;
                return (
                  <td
                    key={comparison.strategies[i]}
                    className={`px-3 py-2 text-right font-mono ${
                      isBest ? "text-[hsl(var(--primary))] font-semibold" : ""
                    }`}
                  >
                    {display}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function OutstandingChart({ chart }) {
  if (!chart?.length) return null;
  const w = 640, h = 180, pad = 32;
  const maxV = Math.max(...chart.map((p) => Math.max(p.baseline, p.with_plan))) || 1;
  const months = chart.length - 1 || 1;
  const x = (i) => pad + (i / months) * (w - pad * 2);
  const y = (v) => h - pad - (v / maxV) * (h - pad * 2);
  const baseline = chart.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.baseline)}`).join(" ");
  const plan = chart.map((p, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(p.with_plan)}`).join(" ");
  return (
    <div className="border hairline rounded-md p-4 bg-card" data-testid="bonus-chart">
      <div className="font-serif text-sm mb-1 flex items-center gap-2">
        <ChartLineUp size={14} weight="duotone" /> Outstanding balance over time
      </div>
      <div className="flex items-center gap-4 text-[11px] mb-2">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-muted-foreground" /> Without prepayment
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block w-3 h-0.5 bg-[hsl(var(--primary))]" /> With recommended plan
        </span>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-auto">
        <line x1={pad} y1={h - pad} x2={w - pad} y2={h - pad} stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        <line x1={pad} y1={pad} x2={pad} y2={h - pad} stroke="currentColor" strokeWidth="0.5" opacity="0.3" />
        <path d={baseline} fill="none" stroke="currentColor" strokeOpacity="0.5" strokeWidth="1.5" />
        <path d={plan} fill="none" stroke="hsl(var(--primary))" strokeWidth="2" />
        <text x={pad - 2} y={pad + 4} fontSize="9" textAnchor="end" opacity="0.6">
          {formatINR(maxV, true)}
        </text>
        <text x={pad - 2} y={h - pad + 2} fontSize="9" textAnchor="end" opacity="0.6">₹0</text>
        <text x={pad} y={h - pad + 14} fontSize="9" opacity="0.6">Now</text>
        <text x={w - pad} y={h - pad + 14} fontSize="9" textAnchor="end" opacity="0.6">
          {formatTenure(months)}
        </text>
      </svg>
    </div>
  );
}

function downloadPdfSummary(result) {
  // Lightweight client-side text summary — the full PDF export happens via
  // existing /api/loans/export.pdf for the saved plan. Here we offer a quick
  // text-only copy as a fallback.
  const txt = [
    "Estima — Bonus / Lump-sum Advisor Summary",
    "=".repeat(45),
    `Prepay date: ${result.prepayment_date}`,
    `Money available: ${formatINR(result.money_available)}`,
    `Preference: ${result.preference}`,
    "",
    "Recommended plan:",
    `  ${result.narrative.headline}`,
    "",
    "Allocations:",
    ...result.recommended.allocations
      .filter((a) => a.pay_now > 0)
      .map((a) => `  ${a.name}: pay ${formatINR(a.pay_now)} · charge ${formatINR(a.charge)} · saves ${formatINR(a.net_saving, true)}${a.closed ? " · CLOSED" : ""}`),
    "",
    result.disclaimer,
  ].join("\n");
  const blob = new Blob([txt], { type: "text/plain" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "Estima_Bonus_Advisor_Summary.txt";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
